import {
  type Block,
  type BlockWireframe,
  CoreBatchAction,
  Factory,
  type ID,
  type NodeItem,
  ProjectActions,
  type SectionItem,
  selectBlocksByNode,
  selectNodeParent,
  selectSectionsByTab,
} from '@octopus-do/octopus-project-core'
import { thunkAddEmptySection } from '@store/migrationActions/thunkActions/thunkSectionsActions'
import type { AppThunk } from '@store/migrationActions/thunkActions/types'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { getId } from '@store/utils'
import {
  commitProjectRequest,
  commitProjectRequestPure,
} from '@storeOld/actionCreators'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import type { Dispatch } from 'redux'

import {
  BASE_COLOR_CLOUDY,
  COLOR_PURPLE,
  COLOR_TOPAZ,
} from '@/constants/constants'
import type { AppState } from '@/types'
import { nextSectionPlacement } from '@/utils/sectionPlacement'

export const setNodeTitle = (id: string, title: string) =>
  ProjectActions.nodes.updateTitle({ id, title })

export const setNodeSeoTitle = (id: string, seoTitle: string) =>
  ProjectActions.nodes.updateNotes({ id, notes: { seoTitle } })

export const setNodeSeoDescription = (id: string, seoDescription: string) =>
  ProjectActions.nodes.updateNotes({ id, notes: { seoDescription } })

export const setNodeSeoH1 = (id: string, seoH1: string) =>
  ProjectActions.nodes.updateNotes({ id, notes: { seoH1 } })

interface AddFilledChildFields {
  id: ID | null
  parentId: ID | null
  colorId: ID | null
  name: string | null
  title: string | null
  url: string | null
  slug: string | null
  h1: string | null
  description: string | null
  keywords: string | null
  pageIntent: string | null
  index: number
  collapsed: boolean
}

// Callers hand over what they actually found and leave the rest out: a page with no H1
// says so with null. The text fields the node stores are strings, so an absent value
// becomes '' here rather than at every call site.
type AddFilledChildParams = Partial<AddFilledChildFields>

export const addFilledChild =
  ({
    id = null,
    parentId = null,
    colorId = null,
    name = null,
    title = null,
    url = null,
    slug = null,
    h1 = null,
    description = null,
    keywords = null,
    pageIntent = null,
    index = 0,
    collapsed = false,
  }: AddFilledChildParams) =>
  (dispatch: Dispatch, getState: () => AppState) => {
    const nodeId = id || getId()

    const activeTab = migrationProjectSelector.getActiveOrFirstTabId(getState())
    const sectionIds = selectSectionsByTab(activeTab)(getState())

    const sectionId = sectionIds[0]

    const node: NodeItem = {
      id: nodeId,
      title: name || title || '',
      collapsed,
      variant: 'default',
      colorId:
        colorId ||
        migrationProjectSelector.getProjectDefaultColorId(getState()),
      // OCT-4991: адрес страницы — только node.url, ниже. В notes его
      // намеренно нет: seoUrl выводится из модели, и новые узлы не должны
      // подкидывать миграции свежие записи для переноса.
      notes: {
        seoTitle: title ?? '',
        seoDescription: description ?? '',
        seoSlug: slug ?? '',
        seoH1: h1 ?? '',
        keywords: keywords ?? '',
        pageIntent: pageIntent ?? '',
      },
      url: url || null,
      image: null,
      figma: null,
    }

    dispatch(
      ProjectActions.nodes.add({
        items: [node],
        index,
        parents: [parentId || sectionId],
      })
    )

    return nodeId
  }

interface AddFilledBlockRequired {
  nodeId: ID
  title: string
}

interface AddFilledBlockOptional {
  id: ID | null
  colorId: ID | null
  wireframes: BlockWireframe[]
  index: number | null
}

type AddFilledBlockParams = Partial<AddFilledBlockOptional> &
  AddFilledBlockRequired

export const addFilledBlock =
  ({
    id = null,
    title,
    nodeId,
    colorId = null,
    wireframes = [],
    index = null,
  }: AddFilledBlockParams): AppThunk =>
  (dispatch, getState) => {
    const blockId = id || getId()

    const defaultColorId = migrationProjectSelector.getProjectDefaultColorId(
      getState()
    )

    const indexToInsert = index ?? selectBlocksByNode(nodeId)(getState()).length

    const block = Factory.block.monolithic({
      id: blockId,
      colorId: colorId || defaultColorId,
      title,
      visual: Factory.block.wireframesVisual({ wireframes }),
    })

    dispatch(
      ProjectActions.blocks.add({
        items: [block],
        index: indexToInsert,
        parents: [nodeId],
      })
    )

    return blockId
  }

export const addFooter =
  (nodeId: string): AppThunk =>
  (dispatch, getState) => {
    if (!nodeId) {
      return
    }

    const color = migrationProjectSelector.getProjectColorByHex(COLOR_PURPLE)(
      getState()
    )

    if (!color) {
      return
    }

    const blocks = selectBlocksByNode(nodeId)(getState())
    const index = blocks.length

    return dispatch(
      addFilledBlock({
        nodeId,
        index,
        title: 'Footer',
        colorId: color.id,
        wireframes: ['footer'],
      })
    )
  }

export const addHeader =
  (nodeId: string): AppThunk =>
  (dispatch, getState) => {
    if (!nodeId) {
      return
    }

    const color = migrationProjectSelector.getProjectColorByHex(COLOR_TOPAZ)(
      getState()
    )

    if (!color) {
      return
    }

    return dispatch(
      addFilledBlock({
        nodeId,
        index: 0,
        title: 'Header',
        colorId: color.id,
        wireframes: ['header'],
      })
    )
  }

export const notifySomethingWentWrong = () =>
  notificationActionCreators.setNotification({
    type: NotificationType.SOMETHING_WENT_WRONG_ERROR,
  })

export const notifyAiInProgress = (onAbort?: () => void, actionText?: string) =>
  notificationActionCreators.setNotification({
    type: NotificationType.AI_GENERATING,
    callback: onAbort,
    actionText,
  })

export const notifyAiGeneratingFinished = () =>
  notificationActionCreators.setNotification({
    type: NotificationType.AI_FINISHED,
  })

export const clearAiNotifications = () =>
  notificationActionCreators.setClearNotification({
    toastContainerId: 'ai',
  })

// OCT-4826 — progress indication while the assistant works.

/**
 * Transient warning shown when the user tries a blocked action (publish, add a
 * tab) while the assistant is still working. Neutral colour, auto-dismisses via
 * the `secondary` container.
 */
export const notifyAiStillWorkingBlocked = () =>
  notificationActionCreators.setCustomNotification({
    type: 'dusk',
    text: 'Octopus Assistant is still working',
    toastContainerId: 'secondary',
  })

/**
 * Shown after the turn has been running for a while: reassures the user work is
 * still going and offers a way out. Lives in the `ai` container until cleared.
 */
export const notifyAiStillWorking = (onStop: () => void) =>
  notificationActionCreators.setCustomNotification({
    type: 'ai',
    text: 'Still working…',
    toastContainerId: 'ai',
    actionText: 'Stop',
    callback: onStop,
  })

/**
 * Shown when the turn is taking anomalously long. Neutral colour so it does
 * not keep shimmering as "in progress"; still offers Stop.
 */
export const notifyAiCouldNotFinish = (onStop: () => void) =>
  notificationActionCreators.setCustomNotification({
    type: 'dusk',
    text: 'Taking longer than usual…',
    toastContainerId: 'ai',
    actionText: 'Stop',
    callback: onStop,
  })

// @todo implement
export const saveGeneratedPages =
  (nodeIds: string[], parentId?: string) =>
  (dispatch: Dispatch, getState: () => AppState) => {
    // todo: fix this action
    const nodes = migrationProjectSelector.getProjectNodesByIds(nodeIds)(
      getState()
    )

    const activeTab = migrationProjectSelector.getActiveOrFirstTabId(getState())
    const sectionIds = selectSectionsByTab(activeTab)(getState())

    const sectionId = sectionIds[0]

    dispatch(
      commitProjectRequestPure(
        CoreBatchAction({
          type: 'ai_generate_nodes',
          actions: nodes.flatMap((node) => {
            const blockIds = selectBlocksByNode(node.id)(getState())
            const blocks = blockIds.map((id) =>
              migrationProjectSelector.getProjectBlockById(id)(getState())
            )

            return [
              ProjectActions.nodes.add({
                items: [node],
                index: 0,
                parents: [parentId || sectionId],
              }),
              ProjectActions.blocks.add({
                items: blocks,
                index: 0,
                parents: [node.id],
              }),
            ]
          }),
        })
      )
    )
  }

/**
 * The section AI Generate fills when it runs inside an open project (OCT-5102), appended
 * to the active tab. A raw action, not a commit, on purpose: the pages stream in as raw
 * actions too, and the whole section goes to the sync server as one batch from
 * saveGeneratedSection once generation is over.
 */
export const addGeneratedSection =
  (title: string): AppThunk<ID> =>
  (dispatch, getState) => {
    const state = getState()
    const activeTab = migrationProjectSelector.getActiveOrFirstTabId(state)
    const { index, order } = nextSectionPlacement(state, activeTab)

    const section: SectionItem = {
      id: getId(),
      title,
      tabId: activeTab,
      colorId: BASE_COLOR_CLOUDY.id,
      order,
      collapsed: false,
      textSize: 's',
    }

    dispatch(
      ProjectActions.sections.add({
        items: [section],
        index,
        parents: [activeTab],
      })
    )

    return section.id
  }

// Appending to a parent whose children were added in generation order — parent before
// child — so "append" is the position every item already holds locally.
const APPEND = -1

/**
 * Commits a section AI Generate streamed into the open project (OCT-5102).
 *
 * Everything already sits in local state as raw actions, hence the pure commit: nothing
 * is applied twice, one batch reaches the sync server, one history entry is written — so
 * one undo takes the whole section back out. Pages are grouped under the parent the
 * relations store knows for them, in generation order, so nesting survives the round
 * trip. saveGeneratedPages hangs every page off a single parent, which a whole sitemap
 * cannot afford.
 */
export const saveGeneratedSection =
  (sectionId: ID, nodeIds: ID[]): AppThunk =>
  (dispatch, getState) => {
    const state = getState()

    const section = state.manager.project.sections.find(
      (item) => item.id === sectionId
    )

    if (!section) {
      return
    }

    const nodesByParent = new Map<ID, NodeItem[]>()
    const blocksByNode = new Map<ID, Block[]>()

    // nodes.add appends, so state order is generation order: parent before child, which
    // is what makes every parent's group precede its children's.
    migrationProjectSelector
      .getProjectNodesByIds(nodeIds)(state)
      .forEach((node) => {
        const parentId = selectNodeParent(node.id)(state)[0] ?? sectionId
        const group = nodesByParent.get(parentId)

        if (group) {
          group.push(node)
        } else {
          nodesByParent.set(parentId, [node])
        }

        const blocks = selectBlocksByNode(node.id)(state).map((id) =>
          migrationProjectSelector.getProjectBlockById(id)(state)
        )

        if (blocks.length) {
          blocksByNode.set(node.id, blocks)
        }
      })

    dispatch(
      commitProjectRequestPure(
        CoreBatchAction({
          type: 'section_creation',
          actions: [
            ProjectActions.sections.add({
              items: [section],
              index: selectSectionsByTab(section.tabId)(state).indexOf(
                sectionId
              ),
              parents: [section.tabId],
            }),
            ...Array.from(nodesByParent, ([parentId, items]) =>
              ProjectActions.nodes.add({
                items,
                index: APPEND,
                parents: [parentId],
              })
            ),
            ...Array.from(blocksByNode, ([nodeId, items]) =>
              ProjectActions.blocks.add({
                items,
                index: APPEND,
                parents: [nodeId],
              })
            ),
          ],
        })
      )
    )
  }

export const saveGeneratedBlocks =
  (blockIds: string[], nodeId: string, index?: number) =>
  (dispatch: Dispatch, getState: () => AppState) => {
    const blocks = blockIds.map((id) =>
      migrationProjectSelector.getProjectBlockById(id)(getState())
    )

    dispatch(
      commitProjectRequestPure(
        CoreBatchAction({
          type: 'ai_generate_nodes',
          actions: [
            ProjectActions.blocks.add({
              items: blocks,
              index: index || 0,
              parents: [nodeId],
            }),
          ],
        })
      )
    )
  }

export const addServicePages = (): AppThunk => async (dispatch) => {
  const nodeIds = []

  const sectionId = (await dispatch(
    thunkAddEmptySection()
  )) as unknown as string

  let nodeId = dispatch(
    addFilledChild({ parentId: sectionId, title: 'Cookies' })
  ) as unknown as string

  nodeIds.push(nodeId)
  dispatch(addHeader(nodeId))
  dispatch(
    addFilledBlock({ title: 'Cookie policy', nodeId, wireframes: ['text'] })
  )
  dispatch(addFooter(nodeId))
  nodeId = dispatch(
    addFilledChild({ parentId: sectionId, title: '404 Error' })
  ) as unknown as string
  nodeIds.push(nodeId)
  dispatch(addHeader(nodeId))
  dispatch(
    addFilledBlock({ title: 'Page not found', nodeId, wireframes: ['image'] })
  )
  dispatch(addFooter(nodeId))

  return nodeIds
}

export const updateBlockAndSave =
  (id: ID, title: string): AppThunk =>
  () =>
    commitProjectRequest(ProjectActions.blocks.updateTitle({ id, title }))
